/**
 * Fase 2 (A) — LISTA de órdenes facturables (admin, cfdi_sales:emit): órdenes
 * CERRADAS del día (o rango) con su estatus de facturación. Facturar /
 * Reintentar abren el MISMO SalesCfdiDialog del POS (emisión y retry reusan el
 * kernel del backend — cero motor nuevo). Vive en Finanzas → Mis facturas.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { SalesCfdiDialog } from './SalesCfdiDialog';

interface FacturableRow {
  order_id: string;
  self_invoice_code: string;
  closed_at: string;
  total: string;
  currency: string;
  source_module: string;
  customer_label: string | null;
  cfdi_id: string | null;
  cfdi_status: string | null;
  folio: string | null;
  error: string | null;
  receptor_rfc: string | null;
  receptor_razon_social: string | null;
}

const STATUS_CLS: Record<string, string> = {
  stamped: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-rose-50 text-rose-700',
  needs_data: 'bg-amber-50 text-amber-700',
  pending: 'bg-slate-100 text-slate-600',
};

const dayStr = (d: Date) => d.toISOString().slice(0, 10);

export function FacturableOrdersCard() {
  const [items, setItems] = useState<FacturableRow[]>([]);
  const [visible, setVisible] = useState(true);
  const [day, setDay] = useState(() => dayStr(new Date()));
  const [dialog, setDialog] = useState<{ mode: 'emit' | 'retry'; row: FacturableRow } | null>(null);

  const load = (d: string) => {
    const from = new Date(`${d}T00:00:00`);
    const to = new Date(from.getTime() + 24 * 3_600_000);
    return api<{ items: FacturableRow[] }>(
      `/cfdi-sales/orders?from=${from.toISOString()}&to=${to.toISOString()}`,
    ).then((r) => setItems(r.items ?? []));
  };

  useEffect(() => {
    load(day).catch(() => setVisible(false)); // sin permiso/módulo → card fuera
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  if (!visible) return null;

  return (
    <div className="card-soft mb-6 rounded-2xl border border-slate-200/80 bg-white">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 px-6 py-5 md:px-8">
        <div>
          <h3 className="font-serif text-lg tracking-tight text-slate-900">Órdenes facturables</h3>
          <p className="text-sm text-slate-500">
            Ventas cerradas del día y su estatus de facturación — factura o reintenta desde aquí.
          </p>
        </div>
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
          className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm text-slate-700" />
      </div>
      <div className="px-6 py-4 md:px-8">
        <div className="max-h-[24rem] overflow-x-auto overflow-y-auto rounded-xl border border-slate-200">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-3 py-2">Hora</th><th className="px-3 py-2">Ticket</th>
                <th className="px-3 py-2">Módulo</th><th className="px-3 py-2">Total</th>
                <th className="px-3 py-2">Facturación</th><th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((o) => (
                <tr key={o.order_id}>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {new Date(o.closed_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {(o.self_invoice_code.match(/.{1,4}/g) ?? []).join('-').toUpperCase()}
                  </td>
                  <td className="px-3 py-2 text-xs">{o.source_module}</td>
                  <td className="px-3 py-2 tabular-nums">
                    ${Number(o.total).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2">
                    {o.cfdi_status ? (
                      <span title={o.error ?? o.folio ?? ''}
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CLS[o.cfdi_status] ?? 'bg-slate-100 text-slate-600'}`}>
                        {o.cfdi_status}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">sin CFDI</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end">
                      {!o.cfdi_status && (
                        <button onClick={() => setDialog({ mode: 'emit', row: o })}
                          className="rounded-lg bg-slate-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-700">
                          Facturar
                        </button>
                      )}
                      {(o.cfdi_status === 'failed' || o.cfdi_status === 'needs_data') && (
                        <button onClick={() => setDialog({ mode: 'retry', row: o })}
                          className="rounded-lg bg-slate-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-slate-700">
                          Reintentar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-slate-400">Sin ventas cerradas este día.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dialog && dialog.mode === 'emit' && (
        <SalesCfdiDialog orderId={dialog.row.order_id}
          onClose={() => { setDialog(null); load(day).catch(() => {}); }} />
      )}
      {dialog && dialog.mode === 'retry' && dialog.row.cfdi_id && (
        <SalesCfdiDialog retryCfdiId={dialog.row.cfdi_id}
          prefill={{ rfc: dialog.row.receptor_rfc, razonSocial: dialog.row.receptor_razon_social }}
          onClose={() => { setDialog(null); load(day).catch(() => {}); }} />
      )}
    </div>
  );
}
